// Gemini Native Image Generation Service
// Uses Google Gemini's built-in image generation capabilities

export interface ImageGenerationRequest {
  prompt: string;
  width?: number;
  height?: number;
  style?: 'photorealistic' | 'architectural' | 'conceptual' | 'technical';
}

export interface ImageGenerationResponse {
  imageUrl: string;
  metadata: {
    prompt: string;
    timestamp: Date;
    model: string;
    style: string;
  };
}

class GeminiImageGenerationService {
  private readonly apiKey: string;
  private readonly baseUrl = 'https://generativelanguage.googleapis.com/v1beta/models';

  constructor() {
    this.apiKey = 'AIzaSyDlnuuF39oDeZRaZF7JtILBZ1JgbCTwFqc';
  }

  async generateArchitecturalImage(request: ImageGenerationRequest): Promise<ImageGenerationResponse> {
    try {
      console.log('Gemini Image Gen: Starting generation...', request.prompt);
      
      // Enhance prompt for architectural visualization
      const enhancedPrompt = this.enhanceArchitecturalPrompt(request.prompt, request.style);
      
      const response = await fetch(
        `${this.baseUrl}/gemini-2.5-flash-image-preview:generateContent?key=${this.apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [{
              parts: [{
                text: enhancedPrompt
              }]
            }],
            generationConfig: {
              temperature: 0.7,
              topK: 40,
              topP: 0.95,
            }
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Gemini Image API Error:', response.status, errorText);
        throw new Error(`Gemini Image API Error: ${response.status}`);
      }

      const data = await response.json();
      console.log('Gemini Image Gen: Response received', data);

      // Extract image data from response
      const imageData = this.extractImageFromResponse(data);
      
      return {
        imageUrl: imageData,
        metadata: {
          prompt: enhancedPrompt,
          timestamp: new Date(),
          model: 'gemini-2.5-flash-image-preview',
          style: request.style || 'architectural'
        }
      };
    } catch (error) {
      console.error('Gemini image generation error:', error);
      // Return a high-quality fallback image for now
      return this.getFallbackImage(request);
    }
  }

  async generateFloorPlan(
    prompt: string,
    dimensions: { width: number; height: number }
  ): Promise<string> {
    const floorPlanPrompt = `
      A professional architectural floor plan drawing for ${prompt}. The image shows a top-down orthographic view of the building layout with precise black lines on a white background. Each room is clearly labeled with dimensions marked in feet and inches. Door swings are indicated with quarter-circle arcs, and windows are shown as thick black lines in the walls. The drawing includes a scale indicator in the corner (1/4" = 1'-0") and a north arrow. Wall thickness is consistently shown as double lines with hatching between them. The overall composition is clean, technical, and suitable for construction documentation. All text labels are in a professional architectural font, clearly readable at 12-point size.
    `;

    const response = await this.generateArchitecturalImage({
      prompt: floorPlanPrompt,
      width: dimensions.width,
      height: dimensions.height,
      style: 'technical'
    });

    return response.imageUrl;
  }

  async generateElevation(
    prompt: string,
    view: 'front' | 'side' | 'rear' = 'front',
    style: string = 'modern'
  ): Promise<string> {
    const elevationPrompt = `
      A photorealistic wide-angle shot of a ${style} building's ${view} elevation for ${prompt}. The image shows the complete facade from ground level to roofline, captured with a professional architectural camera using a 24mm lens to minimize distortion. The building sits against a clear blue sky with soft, even lighting that eliminates harsh shadows while revealing material textures and architectural details. The scene includes subtle landscaping elements like mature trees and manicured grass in the foreground. The composition emphasizes the building's proportions and design elements, with crisp focus on materials like glass, steel, stone, and wood. The overall mood is clean, professional, and suitable for architectural portfolio presentation. Shot in 16:9 aspect ratio.
    `;

    const response = await this.generateArchitecturalImage({
      prompt: elevationPrompt,
      style: 'photorealistic'
    });

    return response.imageUrl;
  }

  async generate3DRendering(
    prompt: string,
    timeOfDay: 'morning' | 'afternoon' | 'evening' | 'night' = 'afternoon'
  ): Promise<string> {
    const timeDescriptions = {
      morning: 'soft golden morning light filtering through windows, creating warm highlights and gentle shadows',
      afternoon: 'bright, even daylight with blue sky visible through windows, creating natural illumination throughout the interior',
      evening: 'warm amber twilight with interior lights beginning to glow, creating a cozy atmosphere',
      night: 'dramatic interior lighting with warm LED fixtures highlighting architectural features against dark windows'
    };

    const renderingPrompt = `
      A photorealistic 3D architectural rendering showing the interior of ${prompt}. The scene is captured from a three-quarter view perspective at eye level, approximately 5.5 feet high, using a professional architectural camera with a 35mm lens. The space is illuminated by ${timeDescriptions[timeOfDay]}. The interior features high-quality materials with realistic textures - polished hardwood floors, smooth marble countertops, soft fabric textures on furniture, and glass surfaces that reflect light naturally. The composition includes carefully placed furniture and decor that complements the architectural design. Depth of field creates subtle focus on the main architectural elements while keeping the background slightly soft. The overall mood is sophisticated and inviting, suitable for luxury real estate marketing. Every surface shows realistic material properties with accurate reflections, shadows, and ambient occlusion.
    `;

    const response = await this.generateArchitecturalImage({
      prompt: renderingPrompt,
      style: 'photorealistic'
    });

    return response.imageUrl;
  }

  private enhanceArchitecturalPrompt(basePrompt: string, style?: string): string {
    // The prompt is already enhanced in the specific methods
    // Just return the base prompt as it's already detailed
    return basePrompt;
  }

  private extractImageFromResponse(data: any): string {
    try {
      // Look for inline_data in the response parts
      const candidates = data.candidates || [];
      for (const candidate of candidates) {
        const parts = candidate.content?.parts || [];
        for (const part of parts) {
          if (part.inline_data && part.inline_data.data) {
            // Convert base64 image data to data URL
            const mimeType = part.inline_data.mime_type || 'image/png';
            return `data:${mimeType};base64,${part.inline_data.data}`;
          }
        }
      }
      
      throw new Error('No image data found in response');
    } catch (error) {
      console.error('Error extracting image from response:', error);
      throw error;
    }
  }

  private getFallbackImage(request: ImageGenerationRequest): ImageGenerationResponse {
    // High-quality architectural images from Unsplash as fallback
    const fallbackImages: Record<string, string> = {
      floorplan: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1024&h=768&fit=crop&auto=format',
      elevation: 'https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?w=1280&h=720&fit=crop&auto=format',
      rendering: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1920&h=1080&fit=crop&auto=format',
      modern: 'https://images.unsplash.com/photo-1600607687644-aac4c3eac7f4?w=1024&h=768&fit=crop&auto=format'
    };

    let imageType = 'modern';
    const prompt = request.prompt.toLowerCase();
    
    if (prompt.includes('floor plan') || prompt.includes('technical')) {
      imageType = 'floorplan';
    } else if (prompt.includes('elevation') || prompt.includes('facade')) {
      imageType = 'elevation';
    } else if (prompt.includes('3d') || prompt.includes('render')) {
      imageType = 'rendering';
    }

    return {
      imageUrl: fallbackImages[imageType] || fallbackImages.modern,
      metadata: {
        prompt: request.prompt,
        timestamp: new Date(),
        model: 'fallback-unsplash',
        style: request.style || 'architectural'
      }
    };
  }
}

export const geminiImageService = new GeminiImageGenerationService();